import { apiClient } from '../../../services/apiClient';
import type { ActorsResponse, Actor, CreateActorRequest, UpdateActorRequest } from './types';

/**
 * AI俳優一覧を取得
 */
export async function listActors(): Promise<ActorsResponse> {
  const { data } = await apiClient.get<ActorsResponse>('/admin/actors');
  return data;
}

/**
 * キャラクター俳優 (ethnicity='character') の顔検査 (face-safe) を実行/再実行する。
 * docs/features/actor-character-support-plan.md §4 P3-b/P4。
 * 機能フラグ (FACE_SCAN_ENABLED) が off の場合は 409 が返る (呼び出し側でエラー表示する)。
 */
export async function triggerActorFaceScan(actorId: string): Promise<{ job_id: string | null; status: string }> {
  const { data } = await apiClient.post<{ job_id: string | null; status: string }>(
    `/admin/actors/${actorId}/face-scan`,
  );
  return data;
}

/**
 * キャラクターシートのポーズを単体で再抽出する (platform admin限定・force=Trueで新しい世代を作る)。
 * docs/features/character-sheet-pose-extraction-plan.md §5。
 * 機能フラグ (POSE_EXTRACTION_ENABLED) がoffの場合は409、シート未登録は400が返る (呼び出し側でエラー表示する)。
 */
export async function requestPoseExtraction(
  actorId: string,
): Promise<{ job_id: string | null; extraction_id: string | null; status: string }> {
  const { data } = await apiClient.post<{ job_id: string | null; extraction_id: string | null; status: string }>(
    `/admin/actors/${actorId}/pose-extraction`,
  );
  return data;
}

/**
 * face-safe バックフィル: 未検査 (status null)・エラー・永久 pending のキャラクター俳優へ
 * 検査ジョブを一括投入する (platform admin 限定)。組織専属のキャラクター俳優も対象に
 * 含まれるため、admin 一覧に表示されない partner 専属キャラの検査導線はこれを使う。
 */
export type FaceScanBackfillReport = {
  enqueued: string[];
  dedup_skipped: string[];
  skipped_missing_sheet: { id: string; reason?: string }[];
  errors: { id: string; error: string }[];
};

export async function runFaceScanBackfill(limit?: number): Promise<FaceScanBackfillReport> {
  const { data } = await apiClient.post<FaceScanBackfillReport>(
    '/admin/actors/face-scan/backfill',
    limit !== undefined ? { limit } : {},
  );
  return data;
}

/**
 * AI俳優を新規作成
 */
export async function createActor(request: CreateActorRequest): Promise<{ actor: Actor }> {
  const formData = new FormData();
  formData.append('character_sheet', request.character_sheet);
  formData.append('name', request.name);
  formData.append('display_name', request.display_name);
  // キャラクター (ethnicity='character') は性別未指定を許可するため、
  // 未指定なら送らない (BE 側は未送信を null 扱いする)。
  if (request.gender) {
    formData.append('gender', request.gender);
  }

  if (request.face_image) {
    formData.append('face_image', request.face_image);
  }
  if (request.display_name_en) {
    formData.append('display_name_en', request.display_name_en);
  }
  if (request.min_age !== undefined) {
    formData.append('min_age', String(request.min_age));
  }
  if (request.max_age !== undefined) {
    formData.append('max_age', String(request.max_age));
  }
  if (request.ethnicity) {
    formData.append('ethnicity', request.ethnicity);
  }
  if (request.tags) {
    formData.append('tags', JSON.stringify(request.tags));
  }
  if (request.body_type) {
    formData.append('body_type', request.body_type);
  }
  if (request.physical_features) {
    formData.append('physical_features', request.physical_features);
  }
  if (request.character_traits) {
    formData.append('character_traits', request.character_traits);
  }
  if (request.generation_prompt) {
    formData.append('generation_prompt', request.generation_prompt);
  }

  const { data } = await apiClient.post<{ actor: Actor }>('/admin/actors', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return data;
}

/**
 * AI俳優を更新
 */
export async function updateActor(
  actorId: string,
  request: UpdateActorRequest
): Promise<{ actor: Actor }> {
  // ファイルがある場合はFormDataを使用
  if (request.character_sheet) {
    const formData = new FormData();
    formData.append('character_sheet', request.character_sheet);

    if (request.display_name !== undefined) {
      formData.append('display_name', request.display_name);
    }
    if (request.gender !== undefined) {
      formData.append('gender', request.gender ?? '');
    }
    if (request.min_age !== undefined) {
      formData.append('min_age', request.min_age === null ? '' : String(request.min_age));
    }
    if (request.max_age !== undefined) {
      formData.append('max_age', request.max_age === null ? '' : String(request.max_age));
    }
    if (request.ethnicity !== undefined) {
      formData.append('ethnicity', request.ethnicity || '');
    }
    if (request.character_traits !== undefined) {
      formData.append('character_traits', request.character_traits ?? '');
    }
    if (request.tags !== undefined) {
      formData.append('tags', JSON.stringify(request.tags));
    }
    if (request.display_order !== undefined) {
      formData.append('display_order', String(request.display_order));
    }

    const { data } = await apiClient.patch<{ actor: Actor }>(
      `/admin/actors/${actorId}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return data;
  }

  // ファイルがない場合はJSONを使用
  const { data } = await apiClient.patch<{ actor: Actor }>(`/admin/actors/${actorId}`, request);
  return data;
}

/**
 * AI俳優を削除
 */
export async function deleteActor(actorId: string): Promise<void> {
  await apiClient.delete(`/admin/actors/${actorId}`);
}

/**
 * AI俳優の表示順を一括更新
 */
export async function reorderActors(
  order: { id: string; display_order: number }[]
): Promise<void> {
  await apiClient.post('/admin/actors/reorder', { order });
}

// === AI俳優生成関連API ===

export type GeneratePersonRequest = {
  prompt: string;
  model?: string;
  gender?: 'male' | 'female';
  age_min?: number;
  age_max?: number;
  ethnicity?: string;
  body_type?: string;
  physical_features?: string;
};

export type GeneratePersonResponse = {
  image_base64: string;
  mime_type: string;
  prompt_used: string;
  /** 生成画像の素材 ID。シート生成・名前提案はこれを参照で渡す (旧バックエンドでは null) */
  material_id: string | null;
};

/** 元画像は source_material_id (人物生成の結果素材) か source_image_base64 (アップロード画像) のどちらか */
export type GenerateCharacterSheetRequest = {
  source_material_id?: string;
  source_image_base64?: string;
  additional_prompt?: string;
  model?: string;
};

export type GenerateCharacterSheetResponse = {
  image_base64: string;
  mime_type: string;
  material_id: string | null;
};

export type SuggestNameAttributes = {
  gender?: 'male' | 'female';
  age_min?: number;
  age_max?: number;
  body_type?: string;
  physical_features?: string;
};

export type SuggestNameRequest = {
  /** 省略可。人物生成の結果素材を参照で渡す */
  image_material_id?: string;
  /** 省略可 (アップロード画像用)。省略時は generation_prompt / attributes からテキストのみで提案する */
  image_base64?: string;
  ethnicity?: string;
  generation_prompt?: string;
  attributes?: SuggestNameAttributes;
};

export type NameSuggestion = {
  display_name: string;
  display_name_en: string;
};

export type SuggestNameResponse = {
  name_suggestions: NameSuggestion[];
  tags: string[];
};

// 同期生成 API は廃止。生成はすべて下記の非同期ジョブ API (*Async) を使う。

// === 非同期ジョブAPI（スマホスリープ対応）===
import {
  generatePersonAsync as generatePersonAsyncApi,
  generateCharacterSheetAsync as generateCharacterSheetAsyncApi,
  suggestNameAsync as suggestNameAsyncApi,
  type AsyncJob,
  type JobWaitOptions,
} from '../../../services/asyncJobApi';

export type { AsyncJob, JobWaitOptions };

/**
 * 名前提案 API のシグネチャ (正本)。
 * ウィザード各層 (ActorManagementPageBase / ActorGenerationWizard / AttributeEditStep) は
 * この型を import して使うこと。個別に型を書くと options の食い違いで
 * AbortSignal が黙って捨てられる (キャンセルが効かない) 事故になる。
 */
export type SuggestNameFn = (
  request: SuggestNameRequest,
  options?: JobWaitOptions,
) => Promise<SuggestNameResponse>;

/**
 * 人物画像を生成（非同期ジョブ版）
 * スマホスリープ時のネットワーク切断に対応
 */
export async function generatePersonAsync(
  request: GeneratePersonRequest,
  options?: { onProgress?: (job: AsyncJob) => void }
): Promise<GeneratePersonResponse> {
  const result = await generatePersonAsyncApi(request, options);
  return {
    image_base64: result.image_base64,
    mime_type: result.mime_type,
    prompt_used: result.prompt_used,
    material_id: result.material_id,
  };
}

/**
 * キャラクターシートを生成（非同期ジョブ版）
 * スマホスリープ時のネットワーク切断に対応
 */
export async function generateCharacterSheetAsync(
  request: GenerateCharacterSheetRequest,
  options?: { onProgress?: (job: AsyncJob) => void }
): Promise<GenerateCharacterSheetResponse> {
  const result = await generateCharacterSheetAsyncApi(request, options);
  return {
    image_base64: result.image_base64,
    mime_type: result.mime_type,
    material_id: result.material_id,
  };
}

/**
 * 名前を提案（非同期ジョブ版）
 * スマホスリープ時のネットワーク切断に対応
 */
export async function suggestNameAsync(
  request: SuggestNameRequest,
  options?: JobWaitOptions
): Promise<SuggestNameResponse> {
  const result = await suggestNameAsyncApi({
    image_material_id: request.image_material_id,
    image_base64: request.image_base64,
    ethnicity: request.ethnicity,
    generation_prompt: request.generation_prompt,
    attributes: request.attributes,
  }, options);

  // バックエンドからの戻り値をそのまま返す
  return {
    name_suggestions: result.name_suggestions.map((s) => ({
      display_name: s.display_name,
      display_name_en: s.display_name_en || '',
    })),
    tags: result.tags || [],
  };
}
